import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Baby, Users, Cake, ShieldCheck, LogOut, Check, Loader2 } from "lucide-react";

const AVATARS = ["🦁", "🦊", "🐼", "🦄", "🐸", "🐙", "🐧", "🦖"];

const FSK_COLOR = { 0: "#8FBC8F", 6: "#87CEEB", 12: "#EEDC82", 16: "#EE9CA1", 18: "#EE5A5A" };

function FskPill({ level }) {
  return (
    <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded" style={{ background: `${FSK_COLOR[level]}22`, color: FSK_COLOR[level] }}>
      FSK {level}
    </span>
  );
}

/**
 * FamilyTab — Kinderprofile mit FSK-Automatik.
 * Der aktuelle Profil-Modus wird im localStorage['mh_kid_profile'] gespeichert,
 * damit andere Komponenten (Landing/Watchlist) darauf filtern können.
 */
export default function FamilyTab() {
  const [children, setChildren] = useState([]);
  const [maxChildren, setMaxChildren] = useState(4);
  const [loading, setLoading] = useState(true);
  const [add, setAdd] = useState(false);
  const [form, setForm] = useState({ name: "", birthday: "", pin: "", avatar: AVATARS[0] });
  const [busy, setBusy] = useState(false);
  const [switchModal, setSwitchModal] = useState(null); // {child}
  const [switchPin, setSwitchPin] = useState("");
  const [activeKid, setActiveKid] = useState(() => {
    try { return JSON.parse(localStorage.getItem("mh_kid_profile") || "null"); } catch { return null; }
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/member/family");
      setChildren(data.children || []);
      setMaxChildren(data.max_children || 4);
    } catch { /* noop */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.name.trim() || !form.birthday || !/^\d{4}$/.test(form.pin)) {
      toast.error("Bitte Name, Geburtstag und 4-stelligen PIN angeben.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/member/family", form);
      toast.success(`${form.name} ist jetzt dabei!`);
      setAdd(false);
      setForm({ name: "", birthday: "", pin: "", avatar: AVATARS[0] });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehler"); }
    finally { setBusy(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Profil von ${c.name} löschen?`)) return;
    try {
      await api.delete(`/member/family/${c.id}`);
      toast.success("Profil entfernt");
      if (activeKid?.id === c.id) exitKidMode();
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehler"); }
  };

  const doSwitch = async () => {
    if (!switchModal) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/member/family/${switchModal.child.id}/switch`, { pin: switchPin });
      localStorage.setItem("mh_kid_profile", JSON.stringify(data));
      setActiveKid(data);
      setSwitchModal(null); setSwitchPin("");
      toast.success(`Hallo ${data.name}! (max FSK ${data.max_fsk})`);
      // Kein automatisches Weiterleiten — Nutzer sieht den Erfolg im Card oben.
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falsche PIN");
      setSwitchPin("");
    } finally { setBusy(false); }
  };

  const exitKidMode = () => {
    localStorage.removeItem("mh_kid_profile");
    setActiveKid(null);
    toast.success("Zurück im Erwachsenen-Profil");
  };

  if (loading) return <div className="text-center py-10 text-[#A8A398]">Lade Familie …</div>;

  return (
    <div className="space-y-6" data-testid="member-family-tab">
      {/* Aktives Kind-Profil Banner */}
      {activeKid && (
        <div className="card-lux p-4 flex items-center gap-3 border-[#8FBC8F]/40" data-testid="family-active-kid">
          <span className="text-3xl">{activeKid.avatar || "🎬"}</span>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest text-[#8FBC8F]">Aktuell im Kind-Modus</div>
            <div className="font-heading text-xl">{activeKid.name} <span className="text-sm text-[#A8A398]">· {activeKid.age} Jahre</span></div>
            <div className="text-xs text-[#A8A398] mt-0.5">Nur Filme bis <FskPill level={activeKid.max_fsk} /> werden angezeigt.</div>
          </div>
          <button onClick={exitKidMode} data-testid="family-exit-kid"
            className="text-[10px] uppercase tracking-widest px-3 py-2 border border-[#575148] text-[#A8A398] rounded hover:border-[#F5F0E6] hover:text-[#F5F0E6]">
            <LogOut size={11} className="inline mr-1" /> Zum Eltern-Profil
          </button>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.35em] text-[#D4AF37] flex items-center gap-2"><Users size={14} /> Familie · {children.length}/{maxChildren}</div>
          <div className="text-xs text-[#A8A398] mt-1">Lege Kinderprofile mit eigenem Geburtstag & PIN an. Die FSK wird automatisch aus dem Alter abgeleitet.</div>
        </div>
        {children.length < maxChildren && (
          <button onClick={() => setAdd(true)} data-testid="family-add-open"
            className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#EEDC82] text-black px-4 py-2 rounded text-[10px] uppercase tracking-widest">
            <Plus size={13} /> Kind hinzufügen
          </button>
        )}
      </div>

      {/* Kinder-Kacheln */}
      {children.length === 0 ? (
        <div className="card-lux p-10 text-center text-[#A8A398]">
          <Baby size={40} className="mx-auto text-[#575148] mb-3" />
          Noch keine Kinderprofile. Leg ein Profil an, damit die Zwerge sich sicher durchs Programm klicken können.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3">
          {children.map((c) => (
            <div key={c.id} data-testid={`family-child-${c.id}`}
              className={`card-lux p-4 relative group ${activeKid?.id === c.id ? "ring-1 ring-[#8FBC8F]" : ""}`}>
              <div className="flex items-start justify-between">
                <span className="text-5xl">{c.avatar || "🎬"}</span>
                <button onClick={() => remove(c)} data-testid={`family-remove-${c.id}`}
                  className="opacity-0 group-hover:opacity-100 text-[#EE9CA1] p-1 rounded hover:bg-[#EE9CA1]/10 transitions-safe">
                  <Trash2 size={13} />
                </button>
              </div>
              <div className="mt-3 font-heading text-xl truncate">{c.name}</div>
              <div className="mt-1 flex items-center gap-2 text-xs text-[#A8A398]">
                <Cake size={12} /> {c.age} J.
                <FskPill level={c.max_fsk} />
              </div>
              <button onClick={() => setSwitchModal({ child: c })} data-testid={`family-switch-${c.id}`}
                className="mt-3 w-full text-[10px] uppercase tracking-widest py-2 rounded border border-[#2A2A2A] hover:border-[#D4AF37] hover:text-[#D4AF37] transitions-safe">
                <ShieldCheck size={11} className="inline mr-1" /> Als {c.name.split(" ")[0]} weiter
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add-Modal */}
      {add && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="family-add-modal">
          <div className="card-lux p-6 max-w-md w-full space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#D4AF37]">Neues Kinderprofil</div>
                <div className="font-heading text-2xl">Wer kommt dazu?</div>
              </div>
              <button onClick={() => setAdd(false)} className="text-[#575148] hover:text-[#F5F0E6] text-xl">×</button>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Avatar</label>
              <div className="mt-2 grid grid-cols-8 gap-1">
                {AVATARS.map((a) => (
                  <button key={a} onClick={() => setForm({ ...form, avatar: a })}
                    className={`text-2xl p-1.5 rounded transitions-safe ${form.avatar === a ? "bg-[#D4AF37]/20 ring-1 ring-[#D4AF37]" : "hover:bg-[#1A1A1A]"}`}>
                    {a}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Name</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="family-add-name"
                className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 outline-none focus:border-[#D4AF37]" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Geburtstag (für FSK-Automatik)</label>
              <input type="date" value={form.birthday} onChange={(e) => setForm({ ...form, birthday: e.target.value })}
                data-testid="family-add-birthday"
                className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 outline-none focus:border-[#D4AF37]" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">4-stelliger PIN (Kind darf ihn merken)</label>
              <input type="text" inputMode="numeric" maxLength={4} value={form.pin}
                onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "").slice(0, 4) })}
                data-testid="family-add-pin"
                placeholder="1234"
                className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 outline-none focus:border-[#D4AF37] font-mono text-center text-2xl tracking-[0.5em]" />
            </div>
            <button onClick={submit} disabled={busy}
              data-testid="family-add-submit"
              className="w-full bg-[#D4AF37] hover:bg-[#EEDC82] text-black py-3 rounded text-[11px] uppercase tracking-widest disabled:opacity-40 inline-flex items-center justify-center gap-2">
              {busy ? <><Loader2 size={14} className="animate-spin" /> Speichere …</> : <><Check size={14} /> Profil anlegen</>}
            </button>
          </div>
        </div>
      )}

      {/* Switch-Modal (PIN) */}
      {switchModal && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="family-switch-modal">
          <div className="card-lux p-6 max-w-sm w-full space-y-4 text-center">
            <span className="text-6xl block">{switchModal.child.avatar}</span>
            <div className="font-heading text-2xl">Hallo {switchModal.child.name}!</div>
            <div className="text-xs text-[#A8A398]">Gib deinen 4-stelligen PIN ein.</div>
            <input type="password" inputMode="numeric" maxLength={4} value={switchPin} autoFocus
              onChange={(e) => setSwitchPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
              onKeyDown={(e) => e.key === "Enter" && doSwitch()}
              data-testid="family-switch-pin"
              className="w-full bg-[#050505] border border-[#2A2A2A] rounded px-3 py-3 outline-none focus:border-[#D4AF37] font-mono text-center text-3xl tracking-[0.6em]" />
            <div className="flex gap-2">
              <button onClick={() => { setSwitchModal(null); setSwitchPin(""); }}
                className="flex-1 py-2.5 rounded border border-[#2A2A2A] text-[#A8A398] text-[10px] uppercase tracking-widest">
                Abbrechen
              </button>
              <button onClick={doSwitch} disabled={busy || switchPin.length !== 4}
                data-testid="family-switch-submit"
                className="flex-1 bg-[#D4AF37] hover:bg-[#EEDC82] text-black py-2.5 rounded text-[10px] uppercase tracking-widest disabled:opacity-40">
                {busy ? "…" : "Los!"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
